// Incidents CP1 — staff incident log + report form. Photos ride the mature
// image pipeline (transcodeImageToWebP) via src/lib/incidentPhotos.ts. The bell
// fires from a DB trigger on insert (no app-level notify call). No detail page
// or edit UI in CP1 — the log is read-only beyond creation; the UPDATE policy +
// audit trigger exist for CP2's amendment flow.

import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate } from '@/lib/utils'
import { Plus, X, AlertTriangle, Truck, Wrench, ClipboardList, User as UserIcon, Image as ImageIcon } from 'lucide-react'
import { toast } from 'sonner'
import { uploadIncidentPhoto, PHOTO_ACCEPT_ATTR } from '@/lib/incidentPhotos'

interface IncidentRow {
  id: string; occurred_at: string; title: string; description: string
  severity: string; status: string; location_text: string | null
  vehicle_id: string | null; field_job_id: string | null; request_id: string | null
  involved_agent_id: string | null; reported_by: string
}

// Severity reuses the platform priority scale (low/normal/high/urgent).
const SEVERITY: Record<string, { label: string; color: string; bg: string }> = {
  low:    { label: 'Low',    color: 'var(--ink-tertiary)', bg: 'var(--surface-sunken)' },
  normal: { label: 'Normal', color: 'var(--info)',         bg: 'var(--info-dim)' },
  high:   { label: 'High',   color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  urgent: { label: 'Urgent', color: 'var(--danger)',       bg: 'var(--danger-dim)' },
}
const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  open:         { label: 'Open',         color: 'var(--info)',         bg: 'var(--info-dim)' },
  under_review: { label: 'Under review', color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  closed:       { label: 'Closed',       color: 'var(--ink-tertiary)', bg: 'var(--surface-sunken)' },
}

const field: React.CSSProperties = { padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', width: '100%' }
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }

export default function IncidentsPage() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'under_review' | 'closed'>('all')
  const [sevFilter, setSevFilter] = useState<'all' | 'low' | 'normal' | 'high' | 'urgent'>('all')
  const [reportOpen, setReportOpen] = useState(false)

  const { data: incidents = [], isLoading, error } = useQuery<IncidentRow[]>({
    queryKey: ['incidents', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('incidents').select('*').eq('workspace_id', workspace.id).order('occurred_at', { ascending: false })
      if (error) { console.error('[incidents] query failed', error); throw error }
      return (data ?? []) as IncidentRow[]
    },
    enabled: !!workspace?.id,
  })

  // Cheap lookup maps for chips (names) + the report-form pickers.
  const { data: vehicles = [] } = useQuery<Array<{ id: string; name: string; status: string }>>({
    queryKey: ['incidents-vehicles', workspace?.id],
    queryFn: async () => (await supabase.from('vehicles').select('id, name, status').eq('workspace_id', workspace!.id).order('name')).data ?? [],
    enabled: !!workspace?.id,
  })
  const { data: jobs = [] } = useQuery<Array<{ id: string; job_number: string | null; title: string | null }>>({
    queryKey: ['incidents-jobs', workspace?.id],
    queryFn: async () => (await supabase.from('field_jobs').select('id, job_number, title').eq('workspace_id', workspace!.id).order('created_at', { ascending: false }).limit(100)).data ?? [],
    enabled: !!workspace?.id,
  })
  const { data: requests = [] } = useQuery<Array<{ id: string; subject: string; status: string }>>({
    queryKey: ['incidents-requests', workspace?.id],
    queryFn: async () => (await supabase.from('portal_requests').select('id, subject, status').eq('workspace_id', workspace!.id).in('status', ['new', 'in_review', 'in_progress']).order('created_at', { ascending: false })).data ?? [],
    enabled: !!workspace?.id,
  })
  const { data: profiles = [] } = useQuery<Array<{ id: string; full_name: string | null }>>({
    queryKey: ['incidents-profiles', workspace?.id],
    queryFn: async () => (await supabase.from('profiles').select('id, full_name').eq('workspace_id', workspace!.id)).data ?? [],
    enabled: !!workspace?.id,
  })
  const { data: photoCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ['incident-photo-counts', workspace?.id],
    queryFn: async () => {
      const { data } = await supabase.from('incident_photos').select('incident_id').eq('workspace_id', workspace!.id)
      const m: Record<string, number> = {}
      for (const r of (data ?? []) as Array<{ incident_id: string }>) m[r.incident_id] = (m[r.incident_id] ?? 0) + 1
      return m
    },
    enabled: !!workspace?.id,
  })

  const vName = useMemo(() => new Map(vehicles.map(v => [v.id, v.name])), [vehicles])
  const jName = useMemo(() => new Map(jobs.map(j => [j.id, j.job_number || j.title || 'Job'])), [jobs])
  const rName = useMemo(() => new Map(requests.map(r => [r.id, r.subject])), [requests])
  const pName = useMemo(() => new Map(profiles.map(p => [p.id, p.full_name ?? 'Unknown'])), [profiles])

  const visible = incidents.filter(i =>
    (statusFilter === 'all' || i.status === statusFilter) &&
    (sevFilter === 'all' || i.severity === sevFilter))

  const chip = (icon: React.ReactNode, text: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--ink-tertiary)' }}>{icon}{text}</span>
  )

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: 'var(--ink-primary)' }}>Incidents</h1>
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>Safety, damage, and injury reports.</p>
        </div>
        <button onClick={() => setReportOpen(true)} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus style={{ width: 16, height: 16 }} /> Report incident
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {(['all', 'open', 'under_review', 'closed'] as const).map(s => (
          <button key={s} type="button" onClick={() => setStatusFilter(s)}
            style={{ padding: '4px 11px', borderRadius: 100, border: '1px solid', borderColor: statusFilter === s ? 'var(--accent)' : 'var(--border-default)', background: statusFilter === s ? 'var(--accent-dim)' : 'transparent', color: statusFilter === s ? 'var(--accent-text)' : 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {s === 'all' ? 'All' : STATUS[s]?.label ?? s}
          </button>
        ))}
        <span style={{ width: 1, background: 'var(--border-subtle)', margin: '0 4px' }} />
        {(['all', 'low', 'normal', 'high', 'urgent'] as const).map(s => (
          <button key={s} type="button" onClick={() => setSevFilter(s)}
            style={{ padding: '4px 11px', borderRadius: 100, border: '1px solid', borderColor: sevFilter === s ? 'var(--accent)' : 'var(--border-default)', background: sevFilter === s ? 'var(--accent-dim)' : 'transparent', color: sevFilter === s ? 'var(--accent-text)' : 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {s === 'all' ? 'Any severity' : SEVERITY[s]?.label ?? s}
          </button>
        ))}
      </div>

      {error ? (
        <div style={{ padding: 20, background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 12, color: 'var(--danger)', fontSize: 13 }}>Couldn’t load incidents. Please refresh.</div>
      ) : isLoading ? (
        <p style={{ color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading…</p>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '56px 20px', border: '1px dashed var(--border-default)', borderRadius: 16 }}>
          <AlertTriangle style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
          <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{incidents.length === 0 ? 'No incidents reported' : 'None match these filters'}</p>
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{incidents.length === 0 ? 'Report an incident to start the log.' : 'Try different filters.'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map(i => {
            const sev = SEVERITY[i.severity] ?? SEVERITY.normal
            const st = STATUS[i.status] ?? STATUS.open
            const pc = photoCounts[i.id] ?? 0
            return (
              <article key={i.id} onClick={() => navigate(`/app/incidents/${i.id}`)}
                role="button" tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/app/incidents/${i.id}`) } }}
                style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${sev.color}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: sev.bg, color: sev.color }}>{sev.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: st.bg, color: st.color }}>{st.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginLeft: 'auto' }}>{formatRelativeDate(i.occurred_at)}</span>
                </div>
                <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{i.title}</p>
                <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 8px', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{i.description}</p>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {i.location_text && chip(<AlertTriangle style={{ width: 11, height: 11 }} />, i.location_text)}
                  {i.vehicle_id && chip(<Truck style={{ width: 11, height: 11 }} />, vName.get(i.vehicle_id) ?? 'Vehicle')}
                  {i.field_job_id && chip(<Wrench style={{ width: 11, height: 11 }} />, jName.get(i.field_job_id) ?? 'Job')}
                  {i.request_id && chip(<ClipboardList style={{ width: 11, height: 11 }} />, rName.get(i.request_id) ?? 'Request')}
                  {i.involved_agent_id && chip(<UserIcon style={{ width: 11, height: 11 }} />, pName.get(i.involved_agent_id) ?? 'Tech')}
                  {pc > 0 && chip(<ImageIcon style={{ width: 11, height: 11 }} />, `${pc} photo${pc > 1 ? 's' : ''}`)}
                  {chip(<UserIcon style={{ width: 11, height: 11 }} />, `by ${pName.get(i.reported_by) ?? 'Unknown'}`)}
                </div>
              </article>
            )
          })}
        </div>
      )}

      <ReportIncidentModal open={reportOpen} onClose={() => setReportOpen(false)}
        onSaved={() => { setReportOpen(false); queryClient.invalidateQueries({ queryKey: ['incidents', workspace?.id] }); queryClient.invalidateQueries({ queryKey: ['incident-photo-counts', workspace?.id] }) }}
        workspaceId={workspace?.id ?? null} vehicles={vehicles} jobs={jobs} requests={requests} profiles={profiles} />
    </div>
  )
}

function ReportIncidentModal({ open, onClose, onSaved, workspaceId, vehicles, jobs, requests, profiles }: {
  open: boolean; onClose: () => void; onSaved: () => void; workspaceId: string | null
  vehicles: Array<{ id: string; name: string; status: string }>
  jobs: Array<{ id: string; job_number: string | null; title: string | null }>
  requests: Array<{ id: string; subject: string; status: string }>
  profiles: Array<{ id: string; full_name: string | null }>
}) {
  const { profile } = useAuth()
  const [occurredAt, setOccurredAt] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState('normal')
  const [vehicleId, setVehicleId] = useState('')
  const [jobId, setJobId] = useState('')
  const [requestId, setRequestId] = useState('')
  const [agentId, setAgentId] = useState('')
  const [locationText, setLocationText] = useState('')
  const [files, setFiles] = useState<File[]>([])

  useEffect(() => {
    if (!open) return
    // datetime-local default = now (local, trimmed to minutes)
    const d = new Date()
    setOccurredAt(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16))
    setTitle(''); setDescription(''); setSeverity('normal'); setVehicleId(''); setJobId(''); setRequestId(''); setAgentId(''); setLocationText(''); setFiles([])
  }, [open])

  const save = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('Not signed in')
      if (!profile?.id) throw new Error('No profile')
      if (!title.trim()) throw new Error('Title is required')
      if (!description.trim()) throw new Error('Description is required')
      if (!occurredAt) throw new Error('Occurred-at is required')
      const { data: inc, error } = await supabase.from('incidents').insert({
        workspace_id: workspaceId,
        occurred_at: new Date(occurredAt).toISOString(),
        title: title.trim(),
        description: description.trim(),
        severity,
        status: 'open',
        location_text: locationText.trim() || null,
        vehicle_id: vehicleId || null,
        field_job_id: jobId || null,
        request_id: requestId || null,
        involved_agent_id: agentId || null,
        reported_by: profile.id,
      }).select('id').single()
      if (error) throw error
      // Photos upload sequentially (simple + reliable at CP1 volume); each goes
      // through transcodeImageToWebP inside the lib. A photo failure is
      // non-fatal — the incident is already filed.
      let failed = 0
      for (const f of files) {
        const { error: upErr } = await uploadIncidentPhoto({ workspaceId, incidentId: inc.id, file: f })
        if (upErr) { console.error('[incidents] photo upload failed', upErr); failed++ }
      }
      if (failed > 0) toast.error(`Incident filed; ${failed} photo${failed > 1 ? 's' : ''} failed to upload`)
    },
    onSuccess: () => { toast.success('Incident reported'); onSaved() },
    onError: (e: any) => toast.error(e?.message || 'Failed to report incident'),
  })

  const canSave = !!title.trim() && !!description.trim() && !!occurredAt && !save.isPending

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }} transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 480, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '92vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>Report incident</h2>
              <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', padding: 2 }}><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label style={lbl}>Occurred at</label><input type="datetime-local" value={occurredAt} onChange={e => setOccurredAt(e.target.value)} style={{ ...field, colorScheme: 'dark' }} /></div>
              <div><label style={lbl}>Severity</label><select value={severity} onChange={e => setSeverity(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select></div>
            </div>
            <div><label style={lbl}>Title</label><input value={title} onChange={e => setTitle(e.target.value)} autoFocus placeholder="Rear-end collision at depot" style={{ ...field, fontWeight: 600 }} /></div>
            <div><label style={lbl}>Description</label><textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="What happened…" style={{ ...field, resize: 'vertical' }} /></div>
            <div><label style={lbl}>Location</label><input value={locationText} onChange={e => setLocationText(e.target.value)} placeholder="Optional — address or site" style={field} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label style={lbl}>Vehicle</label><select value={vehicleId} onChange={e => setVehicleId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">None</option>{vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></div>
              <div><label style={lbl}>Job</label><select value={jobId} onChange={e => setJobId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">None</option>{jobs.map(j => <option key={j.id} value={j.id}>{j.job_number || j.title || 'Job'}</option>)}
              </select></div>
              <div><label style={lbl}>Request</label><select value={requestId} onChange={e => setRequestId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">None</option>{requests.map(r => <option key={r.id} value={r.id}>{r.subject}</option>)}
              </select></div>
              <div><label style={lbl}>Involved tech</label><select value={agentId} onChange={e => setAgentId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">None</option>{profiles.map(p => <option key={p.id} value={p.id}>{p.full_name ?? 'Unnamed'}</option>)}
              </select></div>
            </div>
            <div>
              <label style={lbl}>Photos</label>
              <input type="file" multiple accept={PHOTO_ACCEPT_ATTR} onChange={e => setFiles(Array.from(e.target.files ?? []))}
                style={{ fontSize: 12, color: 'var(--ink-secondary)' }} />
              {files.length > 0 && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>{files.length} file{files.length > 1 ? 's' : ''} attached</p>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button type="button" onClick={onClose} style={{ padding: '8px 14px', background: 'var(--border-subtle)', border: 'none', borderRadius: 8, cursor: 'pointer', color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button type="button" onClick={() => save.mutate()} disabled={!canSave}
                style={{ padding: '8px 16px', background: 'var(--accent)', border: 'none', borderRadius: 8, cursor: canSave ? 'pointer' : 'not-allowed', color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, opacity: canSave ? 1 : 0.5 }}>
                {save.isPending ? 'Reporting…' : 'Report'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
